import useToast from "@repo/core/components/core/Toast";
import { useProposalStore } from "@repo/core/stores/proposalStore";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { apiInstance } from "@repo/core/services/instances/api";
import {
  mapToSecurityDataForm,
  mapToPersonalDataForm,
  mapToAddressDataForm,
  mapToProfessionDataForm,
  mapToFidelityProposalForm,
  mapProposalDetailsToConsultProposal
} from "./mappers";
import {
  SecurityDataFormType,
  PersonalDataFormType,
  AddressDataFormType,
  ProfessionDataFormType,
  FidelityProposalFormType
} from "@repo/core/stores/proposalForm/types";
import {
  UpdateProposalInputDTO,
  PROPOSTA_SITUACAO
} from "@repo/core/services/entities/Proposals";
import { ProposalTypeByCode } from "@repo/core/constants/proposals";
import { normalizeBirthDateToBackendDate } from "@repo/core/utils/birthDate.utils";

const proposalsResource = new ProposalsResource(apiInstance);

export const useProposal = () => {
  const proposalStore = useProposalStore();
  const toast = useToast();

  const getProposalData = async (lngProposta: string) => {
    try {
      const response = await proposalsResource.getProposal(lngProposta);

      if (response.data) {
        // Transform ProposalDetailsResponseDTO to ConsultProposalResponseDTO
        const mappedData = mapProposalDetailsToConsultProposal(response.data);
        proposalStore.setProposalData(mappedData);
        return mappedData;
      }
      throw new Error("No data received from proposal endpoint");
    } catch (error) {
      toast.error(
        "Erro ao buscar dados da proposta",
        "Não foi possível carregar os dados da proposta. Tente novamente."
      );
      throw error;
    }
  };

  const updateProposalData = (
    data: Partial<
      import("@repo/core/services/entities/Proposals").ConsultProposalResponseDTO
    >
  ) => {
    proposalStore.updateProposalData(data);
  };

  /**
   * Removes formatting from CPF/CNPJ (removes dots, dashes, and slashes)
   * Returns only numbers
   */
  const removeCpfCnpjFormatting = (value?: string | null): string => {
    if (!value) return "";
    return value.replace(/[.\-\/]/g, "");
  };

  /**
   * Helper to conditionally include a field only if it has a value
   */
  const includeIfNotEmpty = <T,>(
    value: T | null | undefined | "",
    key: string
  ): Record<string, T> | {} => {
    if (value === null || value === undefined || value === "") {
      return {};
    }
    if (typeof value === "string" && value.trim() === "") {
      return {};
    }
    return { [key]: value } as Record<string, T>;
  };

  const syncProposal = async () => {
    if (!proposalStore.proposalData) {
      toast.error(
        "Erro ao sincronizar proposta",
        "Não há dados da proposta para sincronizar."
      );
      throw new Error("No proposal data available to sync");
    }

    try {
      const proposalData = proposalStore.proposalData;

      const validBirthDate = normalizeBirthDateToBackendDate(
        proposalData.datDataNascimento
      );

      // Get CPF/CNPJ without formatting
      const cpfOrCnpj = proposalData.strCpf || proposalData.strCliente || "";
      const strClienteClean = removeCpfCnpjFormatting(cpfOrCnpj);

      // Build update data object with only fields that exist in the consultation endpoint response
      // and fields that were filled in the form
      const updateData: UpdateProposalInputDTO = {
        strTipoProposta:
          proposalData?.strTipoProposta || ProposalTypeByCode.AMBOS,
        lngProposta: String(proposalData.lngProposta),
        strSituacao: PROPOSTA_SITUACAO.DATA_CONFERENCE, // "20"
        // Fields from consultation endpoint response
        ...includeIfNotEmpty(proposalData.strNome, "strNome"),
        ...includeIfNotEmpty(strClienteClean, "strCliente"),
        ...includeIfNotEmpty(proposalData.strProduto, "strProduto"),
        // Fields filled in the form (even if not in consultation response)
        ...includeIfNotEmpty(proposalData.strEstadoCivil, "strEstCivil"),
        ...includeIfNotEmpty(proposalData.strSexo, "strSexo"),
        ...(validBirthDate && { datNascimento: validBirthDate }),
        // Map strProfissao to strAtivProfissao for the API
        ...includeIfNotEmpty(proposalData.strProfissao, "strAtivProfissao")
      };

      const response = await proposalsResource.editProposal(updateData);

      if (response.data) {
        // Update local store with new situation
        proposalStore.updateProposalData({
          strSituacao: PROPOSTA_SITUACAO.DATA_CONFERENCE
        });

        toast.success(
          "Proposta sincronizada",
          "A proposta foi atualizada com sucesso."
        );

        return response.data;
      }

      throw new Error("No data received from alter proposal endpoint");
    } catch (error) {
      toast.error(
        "Erro ao sincronizar proposta",
        "Não foi possível sincronizar a proposta. Tente novamente."
      );
      throw error;
    }
  };

  const clearProposal = () => {
    proposalStore.resetProposalStore();
  };

  // Helper functions to extract data for forms (optional)
  const getSecurityData = (): SecurityDataFormType | null => {
    if (!proposalStore.proposalData) {
      console.log("useProposal.getSecurityData - proposalData is null");
      return null;
    }
    const mapped = mapToSecurityDataForm(proposalStore.proposalData);
    console.log("useProposal.getSecurityData - mapped data:", mapped);
    return mapped;
  };

  const getPersonalData = (): PersonalDataFormType | null => {
    if (!proposalStore.proposalData) return null;
    return mapToPersonalDataForm(proposalStore.proposalData);
  };

  const getAddressData = (): AddressDataFormType | null => {
    if (!proposalStore.proposalData) return null;
    return mapToAddressDataForm(proposalStore.proposalData);
  };

  const getProfessionData = (): ProfessionDataFormType | null => {
    if (!proposalStore.proposalData) return null;
    return mapToProfessionDataForm(proposalStore.proposalData);
  };

  const getFidelityProposalForm = (): FidelityProposalFormType | null => {
    if (!proposalStore.proposalData) {
      console.log("useProposal.getFidelityProposalForm - proposalData is null");
      return null;
    }
    const mapped = mapToFidelityProposalForm(proposalStore.proposalData);
    console.log("useProposal.getFidelityProposalForm - mapped data:", mapped);
    console.log("useProposal.getFidelityProposalForm - raw proposalData:", {
      datDataNascimento: proposalStore.proposalData.datDataNascimento,
      strEstadoCivil: proposalStore.proposalData.strEstadoCivil,
      strProfissao: proposalStore.proposalData.strProfissao,
      strSexo: proposalStore.proposalData.strSexo
    });
    return mapped;
  };

  return {
    ...proposalStore,
    getProposalData,
    updateProposalData,
    syncProposal,
    clearProposal,
    getSecurityData,
    getPersonalData,
    getAddressData,
    getProfessionData,
    getFidelityProposalForm
  };
};
